
import { Command } from './Command';
import { createStore } from './lib/createStore';
import { createObserve } from './lib/createUseStore';
import { Store, Observe } from './lib/types';

export class Observable<T extends Record<string, any>> {
  /**
   * The current state.
   */
  private _state: T;

  /**
   * The previous state.
   */
  private _prevState: T;

  private store: Store<T>;

  public readonly observe: Observe<Store<T>>; // fka useStore

  /**
   * A list of commands for Undo/Redo.
   */
  private stack: Command[] = [];

  /**
   * Index of the current position in the command stack.
   */
  private pointer = -1;

  constructor(initialState: T) {
    this._state = { ...initialState };
    this._prevState = { ...initialState };
    this.store = createStore(() => this._state);
    this.observe = createObserve(this.store);
  }

  public get snapshot(): T {
    return { ...this._state };
  }

  public get prevSnapshot(): T {
    return { ...this._prevState };
  }

  /**
   * Get whether the state manager can undo.
   */
  private get canUndo(): boolean {
    return this.pointer > -1;
  }

  /**
   * Get whether the state manager can redo.
   */
  private get canRedo(): boolean {
    return this.pointer < this.stack.length - 1;
  }

  protected setState = (newState: Partial<T>, command?: Command | null): any => {
    this._prevState = { ...this._state };
    this._state = { ...this._state, ...newState };
    this.store.setState();
    if (command) this.addToHistory(command);
  };

  /**
   * Adds a command to the undo/redo stack
   */
  public addToHistory(command: Command): void {
    this.stack.push(command);
    this.pointer = this.stack.length - 1;
  }

  /* ------------------- UNDO/REDO ------------------ */

  protected undo(): void {
    if (!this.canUndo) return;

    const cmd = this.stack[this.pointer];
    cmd.undo();
    this.pointer--;
  }

  protected redo(): void {
    if (!this.canRedo) return;

    const cmd = this.stack[this.pointer + 1];
    cmd.redo();
    this.pointer++;
  }
}
